/**
 * Atlas PM · 自动创建任务 · 项目侧数据 hooks（TanStack Query）
 *
 * 写操作成功后统一失效 `autoTaskKeys.all`（前缀匹配全部 task-auto 子查询），
 * 再叠加失效任务 / 项目列表，保证建项目配置、项目详情启停 / 改周期后数字即时刷新。
 *
 * ⚠️ P8.29 起周期下沉到项目订阅：GET/POST /projects/:projectId/tasks 直出条目，
 *    不再有模板级规则（/rules*、/rules/selectable 已全部删除）。
 */

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { qk } from '../../api/queryKeys';
import {
  taskAutoApi,
  type AutoTaskInput,
  type ProjectAutoTaskItem,
  type SaveProjectAutoTaskResult,
} from './autoTaskApi';

export const autoTaskKeys = {
  all: ['task-auto'] as const,
  /** 建项目 picker：可用任务模板（isActive=true） */
  templates: ['task-auto', 'templates'] as const,
  projectTasks: (projectId: string) => ['task-auto', 'projects', projectId, 'tasks'] as const,
};

/** 写操作后失效：task-auto 全量 + 任务列表 / 项目列表（让任务数、地图 KPI 同步） */
function useInvalidate() {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: autoTaskKeys.all }),
      qc.invalidateQueries({ queryKey: ['tasks'] }),
      qc.invalidateQueries({ queryKey: qk.projects() }),
      qc.invalidateQueries({ queryKey: qk.projectMap() }),
    ]);
  };
}

/* ── 建项目 picker：可用任务模板（P8.29：只取启用模板，无 greyout） ── */

/** 启用中的任务模板（isActive=true） */
export function useTaskTemplates() {
  return useQuery({
    queryKey: autoTaskKeys.templates,
    queryFn: async () => {
      const tpl = await taskAutoApi.templates();
      return tpl.items.map((t) => ({ id: t.id, name: t.name }));
    },
    staleTime: 60_000,
  });
}

/* ── 项目自动任务配置（P8.28 起的 /projects/:projectId/tasks 模型） ── */

export function useProjectAutoTasks(projectId: string | undefined) {
  return useQuery({
    queryKey: autoTaskKeys.projectTasks(projectId ?? ''),
    queryFn: () => taskAutoApi.projectTasks(projectId as string),
    enabled: Boolean(projectId),
  });
}

export function useSaveProjectTasks(projectId: string | undefined) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (items: AutoTaskInput[]) =>
      taskAutoApi.saveProjectTasks(projectId as string, items),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateProjectAutoTask(projectId: string | undefined) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof taskAutoApi.updateProjectTask>[2];
    }) => taskAutoApi.updateProjectTask(projectId as string, id, body),
    onSuccess: () => invalidate(),
  });
}

export function useRemoveProjectAutoTask(projectId: string | undefined) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => taskAutoApi.removeProjectTask(projectId as string, id),
    onSuccess: () => invalidate(),
  });
}

export type { AutoTaskInput, ProjectAutoTaskItem, SaveProjectAutoTaskResult };